<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-container">
      <div class="modal-header">
        <h2>Tạo tài khoản mới</h2>
        <button @click="$emit('close')" class="btn-close">
          <i class="icon-x"></i>
        </button>
      </div>

      <form @submit.prevent="handleSubmit" class="modal-body">
        <div class="form-grid">
          <!-- Avatar Upload -->
          <div class="form-group full-width">
            <label>Ảnh đại diện</label>
            <div class="avatar-upload">
              <div class="avatar-preview">
                <img v-if="previewUrl" :src="previewUrl" alt="Preview" />
                <div v-else class="avatar-placeholder">
                  <i class="icon-user"></i>
                </div>
              </div>
              <div class="upload-actions">
                <input
                  type="file"
                  ref="fileInput"
                  @change="handleFileChange"
                  accept="image/*"
                  hidden
                />
                <button type="button" @click="$refs.fileInput.click()" class="btn btn-outline btn-sm">
                  Chọn ảnh
                </button>
                <p class="upload-hint">JPG, PNG tối đa 2MB</p>
              </div>
            </div>
          </div>

          <!-- Name -->
          <div class="form-group">
            <label class="required">Họ và tên</label>
            <input
              v-model="form.name"
              type="text"
              placeholder="Nhập họ và tên"
              :class="{ error: errors.name }"
            />
            <span v-if="errors.name" class="error-message">{{ errors.name }}</span>
          </div>

          <!-- Username -->
          <div class="form-group">
            <label class="required">Tên đăng nhập</label>
            <input
              v-model="form.username"
              type="text"
              placeholder="Nhập tên đăng nhập"
              :class="{ error: errors.username }"
            />
            <span v-if="errors.username" class="error-message">{{ errors.username }}</span>
          </div>

          <!-- Email -->
          <div class="form-group">
            <label class="required">Email</label>
            <input
              v-model="form.email"
              type="email"
              placeholder="Nhập email"
              :class="{ error: errors.email }"
            />
            <span v-if="errors.email" class="error-message">{{ errors.email }}</span>
          </div>

          <!-- Phone -->
          <div class="form-group">
            <label>Số điện thoại</label>
            <input
              v-model="form.phone"
              type="tel"
              placeholder="Nhập số điện thoại"
            />
          </div>

          <!-- Password -->
          <div class="form-group">
            <label class="required">Mật khẩu</label>
            <div class="password-input">
              <input
                v-model="form.password"
                :type="showPassword ? 'text' : 'password'"
                placeholder="Nhập mật khẩu"
                :class="{ error: errors.password }"
              />
              <button type="button" @click="showPassword = !showPassword" class="btn-icon-inline">
                <i :class="showPassword ? 'fa fa-eye-slash' : 'fa fa-eye'"></i>
              </button>
            </div>
            <span v-if="errors.password" class="error-message">{{ errors.password }}</span>
          </div>

          <!-- Confirm Password -->
          <div class="form-group">
            <label class="required">Xác nhận mật khẩu</label>
            <input
              v-model="form.password_confirmation"
              :type="showPassword ? 'text' : 'password'"
              placeholder="Nhập lại mật khẩu"
              :class="{ error: errors.password_confirmation }"
            />
            <span v-if="errors.password_confirmation" class="error-message">
              {{ errors.password_confirmation }}
            </span>
          </div>

          <!-- Role -->
          <div class="form-group">
            <label class="required">Vai trò</label>
            <select v-model="form.role" :class="{ error: errors.role }">
              <option value="">Chọn vai trò</option>
              <option value="super_admin">Super Admin</option>
              <option value="admin">Admin</option>
              <option value="manager">Manager</option>
              <option value="staff">Nhân viên</option>
            </select>
            <span v-if="errors.role" class="error-message">{{ errors.role }}</span>
          </div>

          <!-- Status -->
          <div class="form-group">
            <label class="required">Trạng thái</label>
            <select v-model="form.status">
              <option value="active">Đang hoạt động</option>
              <option value="inactive">Không hoạt động</option>
              <option value="suspended">Tạm ngưng</option>
            </select>
          </div>

          <!-- Permissions -->
          <div class="form-group full-width">
            <label>Quyền</label>
            <div class="permissions-selector">
              <div v-for="group in permissionGroups" :key="group.name" class="permission-group">
                <h4>{{ group.label }}</h4>
                <div class="permission-list">
                  <label
                    v-for="permission in group.permissions"
                    :key="permission.id"
                    class="permission-item"
                  >
                    <input
                      type="checkbox"
                      :value="permission.id"
                      v-model="form.permissions"
                    />
                    <span>{{ permission.display_name }}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>

      <div class="modal-footer">
        <button type="button" @click="$emit('close')" class="btn btn-outline">
          Hủy
        </button>
        <button
          type="button"
          @click="handleSubmit"
          :disabled="loading"
          class="btn btn-primary"
        >
          <span v-if="loading" class="spinner-sm"></span>
          {{ loading ? 'Đang tạo...' : 'Tạo tài khoản' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { adminApi } from '@/services/api';
import { useToast } from '@/composables/useToast';

export default {
  name: 'CreateUserModal',
  emits: ['close', 'created'],
  setup(props, { emit }) {
    const toast = useToast();
    const loading = ref(false);
    const showPassword = ref(false);
    const fileInput = ref(null);
    const previewUrl = ref('');

    const form = reactive({
      name: '',
      username: '',
      email: '',
      phone: '',
      password: '',
      password_confirmation: '',
      role: 'staff',
      status: 'active',
      avatar: null,
      permissions: [],
    });

    const errors = reactive({});
    const permissionGroups = ref([]);

    const fetchPermissions = async () => {
      try {
        const response = await adminApi.getPermissions({ grouped: true });
        const grouped = response.data.data;
        
        permissionGroups.value = Object.keys(grouped).map(key => ({
          name: key,
          label: getGroupLabel(key),
          permissions: grouped[key],
        }));
      } catch (error) {
        console.error('Error fetching permissions:', error);
      }
    };

    const getGroupLabel = (group) => {
      const labels = {
        users: 'Quản lý người dùng',
        customers: 'Quản lý khách hàng',
        products: 'Quản lý sản phẩm',
        orders: 'Quản lý đơn hàng',
        import_export: 'Import/Export',
        reports: 'Báo cáo',
        settings: 'Cài đặt',
      };
      return labels[group] || group;
    };

    const handleFileChange = (event) => {
      const file = event.target.files[0];
      if (file) {
        if (file.size > 2 * 1024 * 1024) {
          toast.error('File không được vượt quá 2MB');
          return;
        }

        form.avatar = file;
        previewUrl.value = URL.createObjectURL(file);
      }
    };

    const validateForm = () => {
      Object.keys(errors).forEach(key => delete errors[key]);

      if (!form.name) errors.name = 'Vui lòng nhập họ tên';
      if (!form.username) errors.username = 'Vui lòng nhập tên đăng nhập';
      if (!form.email) errors.email = 'Vui lòng nhập email';
      if (!form.password) errors.password = 'Vui lòng nhập mật khẩu';
      if (form.password !== form.password_confirmation) {
        errors.password_confirmation = 'Mật khẩu không khớp';
      }
      if (!form.role) errors.role = 'Vui lòng chọn vai trò';

      return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
      if (!validateForm()) {
        toast.error('Vui lòng kiểm tra lại thông tin');
        return;
      }
      loading.value = true;
      try {
        const formData = new FormData();
        Object.keys(form).forEach(key => {
          if (key === 'permissions') {
            form.permissions.forEach(p => formData.append('permissions[]', p));
          } else if (key === 'avatar' && form.avatar) {
            formData.append('avatar', form.avatar);
          } else if (form[key]) {
            formData.append(key, form[key]);
          }
        });

        await adminApi.createUser(formData);
        emit('created');
        emit('close');
      } catch (error) {
        if (error.response?.data?.errors) {
          Object.assign(errors, error.response.data.errors);
        }
        toast.error(error.response?.data?.message || 'Có lỗi xảy ra');
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      fetchPermissions();
    });

    return {
      form,
      errors,
      loading,
      showPassword,
      fileInput,
      previewUrl,
      permissionGroups,
      handleFileChange,
      handleSubmit,
    };
  },
};
</script>
